<template>
  <ContentWrap>
    <!-- 搜索工作栏 -->
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="98px"
    >
      <el-form-item label="客户" prop="customerId">
        <el-select
          v-model="queryParams.customerId"
          class="!w-200px"
          clearable
          filterable
          placeholder="请选择客户"
        >
          <el-option
            v-for="item in customerList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="丢单原因" prop="reasonId">
        <el-select
          v-model="queryParams.reasonId"
          class="!w-200px"
          clearable
          filterable
          placeholder="请选择丢单原因概述"
        >
          <el-option
            v-for="item in reasonList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="项目是否启动" prop="projectStarted">
        <el-select
          v-model="queryParams.projectStarted"
          class="!w-160px"
          clearable
          placeholder="请选择"
        >
          <el-option label="是" :value="true" />
          <el-option label="否" :value="false" />
        </el-select>
      </el-form-item>
      <el-form-item label="选择厂商" prop="vendor">
        <el-input
          v-model="queryParams.vendor"
          class="!w-200px"
          clearable
          placeholder="请输入选择厂商"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="时间" prop="submitTimes">
        <el-date-picker
          v-model="queryParams.submitTimes"
          :default-time="defaultTime"
          class="!w-360px"
          end-placeholder="结束日期"
          range-separator="-"
          start-placeholder="开始日期"
          type="daterange"
          value-format="YYYY-MM-DD HH:mm:ss"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['crm:lost-analysis:create']"
          type="primary"
          @click="openForm('create')"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
        <el-button
          v-hasPermi="['crm:lost-analysis:export']"
          :loading="exportLoading"
          plain
          type="success"
          @click="handleExport"
        >
          <Icon class="mr-5px" icon="ep:download" />
          导出
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list" :show-overflow-tooltip="true" :stripe="true">
      <el-table-column align="center" fixed="left" label="客户" prop="customerName" width="180" />
      <el-table-column align="center" label="联系人" prop="contactName" width="120" />
      <el-table-column align="center" label="丢单原因概述" prop="reasonName" width="180" />
      <el-table-column align="center" label="丢单原因详细说明" prop="reasonDetail" width="260" />
      <el-table-column align="center" label="项目是否启动" prop="projectStartedName" width="130" />
      <el-table-column align="center" label="成交价格" prop="dealPrice" width="130" />
      <el-table-column align="center" label="选择厂商" prop="vendor" width="180" />
      <el-table-column align="center" label="解决方案" prop="solution" width="220" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="时间"
        prop="submitTime"
        width="180"
      />
      <el-table-column align="center" label="申请人" prop="creatorName" width="110" />
      <el-table-column
        align="center"
        fixed="right"
        :show-overflow-tooltip="false"
        label="操作"
        width="120"
      >
        <template #default="scope">
          <div class="crm-followup-action">
            <el-button
              v-hasPermi="['crm:lost-analysis:update']"
              link
              type="primary"
              @click="openForm('update', scope.row.id)"
            >
              编辑
            </el-button>
            <el-button
              v-hasPermi="['crm:lost-analysis:delete']"
              link
              type="danger"
              @click="handleDelete(scope.row.id)"
            >
              删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加/修改 -->
  <LostAnalysisForm ref="formRef" @success="getList" />
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import download from '@/utils/download'
import { LostAnalysisApi, LostAnalysisVO } from '@/api/crm/lostAnalysis'
import { LostReasonApi } from '@/api/crm/lostReason'
import * as CustomerApi from '@/api/crm/customer'
import LostAnalysisForm from './LostAnalysisForm.vue'

defineOptions({ name: 'CrmLostAnalysis' })

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref<LostAnalysisVO[]>([]) // 列表的数据
const customerList = ref<any[]>([]) // 客户下拉
const reasonList = ref<any[]>([]) // 丢单原因概述下拉
const exportLoading = ref(false) // 导出的加载中
/** 日期范围默认带时分秒 00:00:00 ~ 23:59:59 */
const defaultTime = [new Date(2000, 0, 1, 0, 0, 0), new Date(2000, 0, 1, 23, 59, 59)]

const queryParams = reactive<any>({
  pageNo: 1,
  pageSize: 10,
  customerId: undefined,
  reasonId: undefined,
  projectStarted: undefined,
  vendor: '',
  submitTimes: []
})
const queryFormRef = ref() // 搜索的表单

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await LostAnalysisApi.getLostAnalysisPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 重置按钮操作 */
const resetQuery = () => {
  queryFormRef.value.resetFields()
  queryParams.customerId = undefined
  queryParams.reasonId = undefined
  queryParams.projectStarted = undefined
  queryParams.vendor = ''
  queryParams.submitTimes = []
  handleQuery()
}

/** 添加/修改操作 */
const formRef = ref<InstanceType<typeof LostAnalysisForm>>()
const openForm = (type: string, id?: number) => {
  formRef.value?.open(type, id)
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    // 删除的二次确认
    await message.delConfirm()
    // 发起删除
    await LostAnalysisApi.deleteLostAnalysis(id)
    message.success(t('common.delSuccess'))
    // 刷新列表
    await getList()
  } catch {}
}

/** 导出按钮操作 */
const handleExport = async () => {
  try {
    // 导出的二次确认
    await message.exportConfirm()
    // 发起导出
    exportLoading.value = true
    const data = await LostAnalysisApi.exportLostAnalysis(queryParams)
    download.excel(data, '丢单分析.xlsx')
  } catch {
  } finally {
    exportLoading.value = false
  }
}

/** 初始化 **/
onMounted(async () => {
  customerList.value = await CustomerApi.getCustomerSimpleList()
  reasonList.value = await LostReasonApi.getLostReasonListAll()
  await getList()
})
</script>

<style lang="scss" scoped>
.crm-followup-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  :deep(.el-button + .el-button) {
    margin-left: 0;
  }
}
</style>
